/**
 * 送信者メール管理ページ コンテナ
 */
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  type CreateSenderEmailInput,
  type UpdateSenderEmailInput,
  useCreateSenderEmail,
  useDeleteSenderEmail,
  useResendVerification,
  useSenderEmails,
  useUpdateSenderEmail,
} from "../../../hooks/use-sender-emails-api";
import { toastError } from "../../../utils/toast-utils";
import { SenderEmailsPage } from "./SenderEmailsPage";

/**
 * 送信者メール管理ページのコンテナコンポーネント
 * @return {JSX.Element} SenderEmailsPage コンテナ
 */
const SenderEmailsPageContainer: React.FC = () => {
  const { data, loading, error, refetch } = useSenderEmails();
  const { createSenderEmail, loading: isCreating } = useCreateSenderEmail();
  const { updateSenderEmail, loading: isUpdating } = useUpdateSenderEmail();
  const { deleteSenderEmail, loading: isDeleting } = useDeleteSenderEmail();
  const { resendVerification, loading: isResending } = useResendVerification();
  const [actionError, setActionError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle redirect from SES verification email (mount時に1回だけ実行)
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount時のみ実行
  useEffect(() => {
    const verified = searchParams.get("verified");
    if (verified === "true") {
      toast.success("メールアドレスの認証が完了しました");
      void refetch();
    } else if (verified === "false") {
      toastError("認証に失敗しました。再送してください");
    }
    if (verified !== null) {
      setSearchParams({}, { replace: true });
    }
  }, []);

  const handleCreate = useCallback(
    async (input: CreateSenderEmailInput) => {
      setActionError(null);
      try {
        await createSenderEmail(input);
        await refetch();
      } catch (e) {
        // 409 errors are handled as form-level errors in the modal
        if (
          e instanceof Error &&
          "status" in e &&
          (e as { status: number }).status === 409
        ) {
          throw e;
        }
        const msg = e instanceof Error ? e.message : "登録に失敗しました";
        setActionError(msg);
        throw e;
      }
    },
    [createSenderEmail, refetch],
  );

  const handleUpdate = useCallback(
    async (id: string, input: UpdateSenderEmailInput) => {
      setActionError(null);
      try {
        await updateSenderEmail(id, input);
        await refetch();
      } catch (e) {
        const msg = e instanceof Error ? e.message : "更新に失敗しました";
        setActionError(msg);
      }
    },
    [updateSenderEmail, refetch],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      setActionError(null);
      try {
        await deleteSenderEmail(id);
        await refetch();
      } catch (e) {
        const msg = e instanceof Error ? e.message : "削除に失敗しました";
        setActionError(msg);
      }
    },
    [deleteSenderEmail, refetch],
  );

  const handleResendVerification = useCallback(
    async (id: string) => {
      setActionError(null);
      try {
        await resendVerification(id);
        await refetch();
      } catch (e) {
        const msg = e instanceof Error ? e.message : "再送に失敗しました";
        setActionError(msg);
      }
    },
    [resendVerification, refetch],
  );

  const handleRefresh = useCallback(async () => {
    setActionError(null);
    await refetch();
  }, [refetch]);

  return (
    <SenderEmailsPage
      senderEmails={data?.items ?? []}
      loading={loading}
      error={error ? { message: error.message } : null}
      actionError={actionError}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      onResendVerification={handleResendVerification}
      onRefresh={handleRefresh}
      isCreating={isCreating}
      isUpdating={isUpdating}
      isDeleting={isDeleting}
      isResending={isResending}
    />
  );
};

export default SenderEmailsPageContainer;
